import { useContext, useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaSquareWhatsapp } from "react-icons/fa6";
import {
  FaInstagramSquare,
  FaFacebookSquare,
  FaPinterestSquare,
} from "react-icons/fa";
import RelatedProducts from "../../components/relatedproducts/RelatedProducts";
import { FaShareAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import ContactUsForm from "../../components/contactusproduct/contactusproduct";
import { LanguageContext } from "../../context/LanguageContext";
import { getLimitedImages } from "../../config/pageDetails";

import "./productDetails.css";

const ProductsDetails = () => {
  const { t } = useContext(LanguageContext);
  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const [productReference, setProductReference] = useState({});
  const [productDetail, setProductDetail] = useState({});

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef(null);

  const queryFormRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const productImages = useMemo(
    () =>
      getLimitedImages(
        productDetail?.imageUrl,
        "productDetails",
        "galleryImageLimit"
      ),
    [productDetail?.imageUrl]
  );
  const selectedImage = productImages[selectedImageIndex];

  const handleBreadcrumb = (name) => {
    if (name === "Category") {
      navigate("/category");
    } else if (name === "CategoryName") {
      const slide = {
        categoryId: productReference.categoryId,
        subCategoryId: productReference.subCategoryId,
        name: productReference.categoryName,
        metaData: productReference.categoryMetaData,
      };
      navigate("/subcategory", { state: { slide } });
    } else if (name === "SubCategoryName") {
      const subCategory = {
        categoryId: productReference.categoryId,
        subCategoryId: productReference.subCategoryId,
        name: productReference.subCategoryName,
        metaData: productReference.subCategoryMetadata,
      };
      navigate("/products", { state: { subCategory } });
    }
  };

  useEffect(() => {
    if (!id) {
      navigate(`/category`);
      return;
    }

    const product = location.state?.product;
    const bodyData = product && product.categoryId && product.subCategoryId ? { categoryId: product.categoryId, subCategoryId: product.subCategoryId } : {};

    const fetchProductData = async () => {
      try {
        const response = await fetch(`/api/products/get/${id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bodyData),
        });

        if (!response.ok) {
          console.error("Failed to fetch product data");
          return;
        }

        const result = await response.json();

        setProductReference(result.productReference || {});
        setProductDetail(result.productDetails || {});
      } catch (err) {
        console.error(err);
      }
    };

    fetchProductData();
  }, [id, location.state, navigate]);

  useEffect(() => {
    if (selectedImageIndex >= productImages.length) {
      setSelectedImageIndex(0);
    }
  }, [productImages.length, selectedImageIndex]);

  useEffect(() => {
    if (productImages.length === 0) return;

    const startSlideshow = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!isHovered) {
          setSelectedImageIndex(
            (prev) => (prev + 1) % productImages.length
          );
        }
      }, 5000);
    };

    startSlideshow();

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [productImages.length, isHovered]);

  const handleEnquiryClick = () => {
    if (queryFormRef.current) {
      queryFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleWhatsappClick = () => {
    const phoneNumber = "918905046144";
    const message = `Hello, I am interested in the product: ${productDetail.name} \nProduct Link: ${window.location.href}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareOnWhatsapp = () => {
    const shareText = `Check out this amazing product: ${productDetail.name}\n\nView here: ${window.location.href}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareOnFacebook = () => {
    window.open("https://www.facebook.com/Harshpandey2161", "_blank");
  };

  const handleShareOnPinterest = () => {
    window.open("https://www.pinterest.com/harshpandey9079", "_blank");
  };

  const handleShareOnInstagram = () => {
    window.open("https://www.instagram.com/eescapewithme_/", "_blank");
  };

  const shareProduct = async () => {
    const productURL = window.location.href;

    try {
      await navigator.clipboard.writeText(productURL);

      if (navigator.share) {
        await navigator.share({
          title: productDetail.name,
          text: `Check out this amazing product: ${productDetail.name}`,
          url: productURL,
        });
      }
    } catch (err) {
      console.error("Error sharing:", err);
      toast.success("Product link copied to clipboard!", {
        position: "top-right",
        theme: "colored",
      });
    }
  };

  return (
    productReference &&
    productDetail && (
      <div className="product-details-container">
        {isFullscreen && (
          <div
            className="fullscreen-overlay"
            onClick={() => setIsFullscreen(false)}
          >
            <div
              className="fullscreen-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="close-btn-large-image"
                onClick={() => setIsFullscreen(false)}
              >
                ×
              </button>
              <img
                src={selectedImage}
                alt="Fullscreen"
                className="fullscreen-image"
              />
            </div>
          </div>
        )}

        <div className="product-main">
          <div
            className="product-image-section"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {
              <div
                className="main-image-wrapper"
                style={{ position: "relative" }}
              >
                <img
                  src={selectedImage}
                  alt="Main Display"
                  className="main-image"
                  onClick={() => setIsFullscreen(true)}
                />
                <FaShareAlt
                  className="download-icon-subcategory"
                  onClick={shareProduct}
                />
              </div>
            }

            <div className="thumbnail-images">
              {productImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Thumb ${index}`}
                  onClick={() => setSelectedImageIndex(index)}
                  className={
                    index === selectedImageIndex ? "active-thumbnail" : ""
                  }
                />
              ))}
            </div>
          </div>

          <div className="product-info-section">
            <div className="breadcrumb-product-container">
              {" "}
              <div
                className="breadcrumb-product"
                onClick={() => handleBreadcrumb("Category")}
              >
                {" "}
                Category{" "}
              </div>{" "}
              <div className="breadcrumb-product">/</div>{" "}
              <div
                className="breadcrumb-product"
                onClick={() => handleBreadcrumb("CategoryName")}
              >
                {" "}
                {productReference.categoryName}{" "}
              </div>{" "}
              <div className="breadcrumb-product">/</div>{" "}
              <div
                className="breadcrumb-product"
                onClick={() => handleBreadcrumb("SubCategoryName")}
              >
                {" "}
                {productReference.subCategoryName}{" "}
              </div>
            </div>
            <div className="product-info-upper">
              <h2 className="product-title">{productDetail.name}</h2>

              <div className="product-stock-status">
                <span className="stock-pill">
                  <svg
                    className="stock-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="green"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m7 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="stock-text">
                    {t(`productDetails.inStock`)}
                  </span>
                </span>
              </div>

              <p className="description">{productDetail.description}</p>

              <div className="partion-line"></div>

              <div className="vouchers">
                <div className="voucher">
                  <strong>{t(`constants.size`)}</strong>:{" "}
                  {t(`productDetails.sizeMessage`)}
                </div>
                <div className="voucher">
                  <strong>{t(`constants.material`)}</strong>:{" "}
                  {t(`productDetails.materialMessage`)}
                </div>
              </div>

              <div className="partion-line"></div>

              <div className="product-cta">
                <button
                  className="cta-btn enquiry-btn"
                  onClick={handleEnquiryClick}
                >
                  <span className="cta-icon">📞</span>
                  {t("productDetails.inquireNow")}
                </button>

                <button
                  className="cta-btn whatsapp-btn"
                  onClick={handleWhatsappClick}
                >
                  <span className="cta-icon">💬</span>
                  {t("productDetails.whatsappInquiry")}
                </button>
              </div>
            </div>

            <div className="product-share">
              <span className="share-label">
                {t("productDetails.shareProductOn")}:
              </span>

              <div className="share-icons">
                <span
                  className="share-circle whatsapp"
                  onClick={handleShareOnWhatsapp}
                >
                  <FaSquareWhatsapp />
                </span>

                <span
                  className="share-circle instagram"
                  onClick={handleShareOnInstagram}
                >
                  <FaInstagramSquare />
                </span>

                <span
                  className="share-circle facebook"
                  onClick={handleShareOnFacebook}
                >
                  <FaFacebookSquare />
                </span>

                <span
                  className="share-circle pinterest"
                  onClick={handleShareOnPinterest}
                >
                  <FaPinterestSquare />
                </span>
              </div>
            </div>

            <div className="product-call-us">
              <p>
                {t(`productDetails.callUsFree`)}:
                <div>
                  <div>
                    <a href="tel:+918905046144">(+91) 7891 676 144</a>
                  </div>
                  <div>
                    <a href="tel:+919413963203">(+91) 9413 963 203</a>
                  </div>
                </div>
              </p>
            </div>

            <div className="product-call-us">
              <p>
                {t(`productDetails.emailUsFree`)}:
                <div>
                  <div>
                    <a href="mailto:harshpandey9079@gmail.com?subject=Marble Statue Enquiry&body=Hello, I am interested in your marble statues. Please provide more details. Product Link: ">
                      harshpandey9079@gmail.com
                    </a>
                  </div>
                  <div>
                    <a href="mailto:rajeshpandey2161@gmail.com?subject=Marble Statue Enquiry&body=Hello, I am interested in your marble statues. Please provide more details.">
                      rajeshpandey2161@gmail.com
                    </a>
                  </div>
                </div>
              </p>
            </div>
          </div>
        </div>

        <div className="query-form-wrapper" ref={queryFormRef}>
          <ContactUsForm />
        </div>

        {productReference && (
          <RelatedProducts productReference={productReference} />
        )}
      </div>
    )
  );
};

export default ProductsDetails;
